import {
  CART_SESSION_COOKIE_CREATED_AT,
  CART_SESSION_COOKIE_NAME,
  getOrCreateCart,
  fetchGraphQL,
} from '../storefront-cart';

import { deleteCookie, getCookie, setCookie } from '../cookie';

const UPDATE_CART_ITEMS_MUTATION = `#graphql
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 250) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                  sku
                  title
                  price {
                    amount
                  }
                  compareAtPrice {
                    amount
                 }
                  product {
                    title
                    vendor
                    productType
                    handle
                  }
                  image {
                    src
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                  }
                }
              }
              quantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                compareAtAmountPerQuantity {
                  amount
                  currencyCode
                }
              }
              attributes {
                key
                value
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
              }
              __typename
              ... on ComponentizableCartLine {
                lineComponents {
                  id
                  quantity
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    compareAtAmountPerQuantity {
                      amount
                      currencyCode
                    }
                  }
                  merchandise {
                    __typename
                    ... on ProductVariant {
                      id
                      sku
                      title
                      price {
                        amount
                      }
                      product {
                        title
                        vendor
                        productType
                        handle
                      }
                      image {
                        src
                        altText
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                  sellingPlanAllocation {
                    sellingPlan {
                      id
                      name
                    }
                    priceAdjustments {
                      price {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
        discountCodes {
          code
        }
        attributes {
          key
          value
        }
        note
        checkoutUrl
        cost {
          checkoutChargeAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
        discountAllocations {
          discountedAmount {
            amount
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const ADD_CART_ITEMS_MUTATION = `#graphql
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 250) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                  sku
                  title
                  price {
                    amount
                  }
                  compareAtPrice {
                    amount
                  }
                  product {
                    title
                    vendor
                    productType
                    handle
                  }
                  image {
                    src
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
              quantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
                subtotalAmount {
                  amount
                  currencyCode
                }
                compareAtAmountPerQuantity {
                  amount
                  currencyCode
                }
              }
              attributes {
                key
                value
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
              }
              __typename
              ... on ComponentizableCartLine {
                lineComponents {
                  id
                  quantity
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    compareAtAmountPerQuantity {
                      amount
                      currencyCode
                    }
                  }
                  merchandise {
                    __typename
                    ... on ProductVariant {
                      id
                      sku
                      title
                      price {
                        amount
                      }
                      product {
                        title
                        vendor
                        productType
                        handle
                      }
                      image {
                        src
                        altText
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                  sellingPlanAllocation {
                    sellingPlan {
                      id
                      name
                    }
                    priceAdjustments {
                      price {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
        discountCodes {
          code
        }
        attributes {
          key
          value
        }
        note
        checkoutUrl
        cost {
          checkoutChargeAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
        discountAllocations {
          discountedAmount {
            amount
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const UPDATE_DISCOUNT_CODES_MUTATION = `#graphql
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const removeItem = async (id: number | string): Promise<any> => {
  return updateItem(id, 0);
};

export function api_url(path: string): string {
  return window.Shopify.routes.root + path.replace(/^\//, '');
}

export const updateItem = async (
  id: number | string,
  quantity: number | string
): Promise<any> => {
  let cartId = getCookie(CART_SESSION_COOKIE_NAME);

  if (!cartId) {
    await getOrCreateCart();
    cartId = getCookie(CART_SESSION_COOKIE_NAME);
  }

  if (!cartId) {
    return;
  }

  const linesToUpdate = [
    {
      id: id.toString(),
      quantity: parseInt(quantity.toString()),
    },
  ];

  const { cartLinesUpdate } = await fetchGraphQL(UPDATE_CART_ITEMS_MUTATION, {
    cartId,
    lines: linesToUpdate,
  });

  if (cartLinesUpdate.userErrors.length === 0) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: cartLinesUpdate.cart,
        },
      })
    );
    return cartLinesUpdate.cart;
  } else {
    throw new Error(
      cartLinesUpdate.userErrors.map((e: Error) => e.message).join(', ')
    );
  }
};

export const addItem = async (
  variantId: string,
  quantity = 1,
  sellingPlanId?: string
): Promise<any> => {
  const checkout = await getOrCreateCart();
  const line = {
    merchandiseId: variantId,
    quantity,
  } as any;

  if (sellingPlanId) {
    line.sellingPlanId = sellingPlanId;
  }

  const { cartLinesAdd } = await fetchGraphQL(ADD_CART_ITEMS_MUTATION, {
    cartId: checkout.id,
    lines: [line],
  });

  if (cartLinesAdd.userErrors.length === 0) {
    return cartLinesAdd.cart;
  } else {
    throw new Error(
      cartLinesAdd.userErrors.map((e: Error) => e.message).join(', ')
    );
  }
};

export const applyDiscountCode = async (code: string): Promise<any> => {
  let cartId = getCookie(CART_SESSION_COOKIE_NAME);

  if (!cartId) {
    await getOrCreateCart();
    cartId = getCookie(CART_SESSION_COOKIE_NAME);
  }

  if (!cartId) {
    throw new Error('No cart found');
  }

  const { cartDiscountCodesUpdate } = await fetchGraphQL(
    UPDATE_DISCOUNT_CODES_MUTATION,
    {
      cartId,
      discountCodes: [code],
    }
  );

  if (cartDiscountCodesUpdate.userErrors.length === 0) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: cartDiscountCodesUpdate.cart,
        },
      })
    );
    return cartDiscountCodesUpdate.cart;
  } else {
    throw new Error(
      cartDiscountCodesUpdate.userErrors.map((e: any) => e.message).join(', ')
    );
  }
};

document.addEventListener('DOMContentLoaded', () => {
  const url = window.location.href;
  const params = new URLSearchParams(url.split('?')[1]);
  const forceCheckoutTokenCycle = params.get('forceCheckoutTokenCycle');

  if (forceCheckoutTokenCycle === 'true') {
    // setting the value to 0 will force the checkout token to be cycled on
    // the next interaction
    setCookie(CART_SESSION_COOKIE_CREATED_AT, '0');
    // setting the expiration date to the past will ensure it is gone from the
    // browser on next page load if user doesn't perform any actions now.
    deleteCookie(CART_SESSION_COOKIE_CREATED_AT);
  }
});
